import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { Realtime } from '@/lib/api/v1';
import { apiBase } from '@/lib/api/client';
import { getClientId } from '@/lib/client-id';
import { useSession, useToasts } from '@/lib/store';

type ToastTone = 'success' | 'danger' | 'info' | 'warning';

type RealtimeEvent = {
  id: string;
  type: string;
  tenantId: string;
  createdAt: string;
  actor?: {
    userId: string;
    email?: string | null;
    name?: string;
  };
  sourceClientId?: string;
  toast?: {
    title: string;
    description?: string;
    tone?: ToastTone;
    quiet?: boolean;
    dedupeKey?: string;
  };
  payload?: unknown;
};

type RealtimeBridgeProps = {
  queryClient: QueryClient;
};

const recentToasts = new Map<string, number>();

export function RealtimeBridge({ queryClient }: RealtimeBridgeProps) {
  const user = useSession((state) => state.user);
  const pushToast = useToasts((state) => state.push);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let socket: Socket | null = null;
    const clientId = getClientId();

    Realtime.session()
      .then((session) => {
        if (cancelled) return;
        socket = io(socketOrigin(), {
          path: '/socket.io',
          transports: ['websocket'],
          withCredentials: true,
          auth: {
            clientType: 'app',
            token: session.token,
            clientId,
          },
        });
        socket.on('realtime:event', (event: RealtimeEvent) => {
          handleRealtimeEvent(queryClient, event);
          maybeShowToast(event, clientId, pushToast);
        });
      })
      .catch(() => {
        /* Realtime is best-effort; normal API polling still works. */
      });

    return () => {
      cancelled = true;
      socket?.disconnect();
    };
  }, [queryClient, pushToast, user]);

  return null;
}

function socketOrigin(): string {
  return new URL(apiBase || window.location.origin, window.location.origin).origin;
}

function handleRealtimeEvent(queryClient: QueryClient, event: RealtimeEvent): void {
  if (event.type.startsWith('cellar:')) {
    void queryClient.invalidateQueries({ queryKey: ['cellar.bottles'] });
    void queryClient.invalidateQueries({ queryKey: ['racks.bottles'] });
    void queryClient.invalidateQueries({ queryKey: ['recommendations'] });
    void queryClient.invalidateQueries({ queryKey: ['stats'] });

    const bottleId = readBottleId(event.payload);
    if (bottleId) {
      void queryClient.invalidateQueries({ queryKey: ['bottle', bottleId] });
      void queryClient.invalidateQueries({ queryKey: ['bottle.notes', bottleId] });
    }
  }

  if (event.type.startsWith('racks:')) {
    void queryClient.invalidateQueries({ queryKey: ['racks'] });
    void queryClient.invalidateQueries({ queryKey: ['racks.bottles'] });
  }

  if (event.type.startsWith('scan:')) {
    void queryClient.invalidateQueries({ queryKey: ['scans'] });
  }

  if (event.type.startsWith('labels:')) {
    void queryClient.invalidateQueries({ queryKey: ['labels.queue'] });
    void queryClient.invalidateQueries({ queryKey: ['labels.list'] });
    void queryClient.invalidateQueries({ queryKey: ['print.status'] });
  }

  if (event.type.startsWith('auth:')) {
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['friends'] });
  }
}

function maybeShowToast(
  event: RealtimeEvent,
  clientId: string,
  pushToast: (toast: { title: string; description?: string; tone?: ToastTone }) => void,
): void {
  const toast = event.toast;
  if (!toast || toast.quiet || event.sourceClientId === clientId) return;

  const key = event.type.startsWith('scan:')
    ? `scan:${toast.tone ?? 'info'}`
    : toast.dedupeKey ?? event.type;
  const cooldownMs = event.type.startsWith('scan:') ? 3500 : 1500;
  const now = Date.now();
  const previous = recentToasts.get(key) ?? 0;
  if (now - previous < cooldownMs) return;
  recentToasts.set(key, now);

  const actor = event.actor?.name || event.actor?.email;
  pushToast({
    title: actor ? `${actor}: ${toast.title}` : toast.title,
    description: toast.description,
    tone: toast.tone,
  });
}

function readBottleId(payload: unknown): string | undefined {
  const record = asRecord(payload);
  const bottle = asRecord(record.bottle);
  const bottleId = typeof bottle.id === 'string' ? bottle.id : undefined;
  if (bottleId) return bottleId;
  return typeof record.bottleId === 'string' ? record.bottleId : undefined;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}
